/**
 * Cross-origin session handoff for the embedded Aura iframe.
 *
 * ROOT CAUSE this exists to solve:
 *
 * The platform sets `lumina_token` with no `domain` attribute and `SameSite=lax`
 * (server/luminaAuthBridge.ts). That makes it a **host-only** cookie on
 * luminacorp.io. It works for AionUI, which is embedded at the same-origin path
 * `/aion`, and the comment there says "for iframe auto-login" — but Aura is
 * embedded from `https://aura.luminacorp.io`, a different host, so
 * `document.cookie` in the iframe cannot see it and never will.
 *
 * Rather than widen the cookie to `.luminacorp.io` with `SameSite=None` — which
 * would expose the session to every current and future subdomain, on every
 * cross-site request — the parent hands the token to this specific frame and
 * nothing else changes.
 *
 * Protocol (parent = platform, child = Aura):
 *   child  -> parent : { type: 'lumina:request-token' }
 *   parent -> child  : { type: 'lumina:token', token }
 *
 * The child asks rather than only listening, because the parent may post before
 * the iframe's script is running; asking makes the handoff independent of load
 * order.
 */

const TOKEN_MESSAGE = 'lumina:token'
const REQUEST_MESSAGE = 'lumina:request-token'

/** Where a token is allowed to come from. Anything else is ignored. */
const TRUSTED_PARENT_ORIGINS = [
  'https://luminacorp.io',
  'https://www.luminacorp.io',
]

/**
 * Storage key already read by `getLuminaSessionToken()`. Persisting means a
 * reload inside the iframe does not have to wait for another handshake.
 */
const TOKEN_STORAGE_KEY = 'lumina_jwt'

function isTrustedOrigin(origin: string): boolean {
  if (TRUSTED_PARENT_ORIGINS.includes(origin))
    return true
  // Same-origin delivery, for when Aura is served from the platform itself.
  return typeof window !== 'undefined' && origin === window.location.origin
}

/**
 * A JWT is three dot-separated base64url segments. This is a shape check, not a
 * verification: the token is opaque to Aura and only the proxy and the auth
 * service validate it. The check exists so a malformed or hostile message
 * cannot poison storage with a value we would then replay as a bearer.
 */
function looksLikeJwt(value: unknown): value is string {
  return typeof value === 'string' && /^[\w-]+\.[\w-]+\.[\w-]+$/.test(value)
}

function acceptToken(token: string): void {
  ;(window as unknown as { __LUMINA_JWT__?: string }).__LUMINA_JWT__ = token
  try {
    localStorage.setItem(TOKEN_STORAGE_KEY, token)
  }
  catch {
    // Private mode or blocked storage: the in-memory value still serves this
    // session, so this is not worth failing the handshake over.
  }
}

/**
 * Starts listening for the parent's token and asks for one.
 *
 * Safe to call when not embedded: `window.parent === window`, so no message is
 * sent and Aura falls back to the cookie/localStorage paths.
 *
 * @returns a function that removes the listener.
 */
export function initLuminaTokenBridge(onToken?: (token: string) => void): () => void {
  if (typeof window === 'undefined')
    return () => {}

  const handler = (event: MessageEvent) => {
    if (!isTrustedOrigin(event.origin))
      return

    const data = event.data as { type?: unknown, token?: unknown } | null
    if (!data || data.type !== TOKEN_MESSAGE)
      return

    if (!looksLikeJwt(data.token))
      return

    acceptToken(data.token)
    onToken?.(data.token)
  }

  window.addEventListener('message', handler)

  if (window.parent !== window) {
    // Targeted per trusted origin rather than '*': this message names no secret,
    // but broadcasting the fact that a session is wanted is still needless.
    for (const origin of TRUSTED_PARENT_ORIGINS)
      window.parent.postMessage({ type: REQUEST_MESSAGE }, origin)
  }

  return () => window.removeEventListener('message', handler)
}
